import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useSession } from '../auth/queries';
import type { SavedView } from './api';
import {
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
  useUpdateSavedView,
} from './queries';

interface SavedViewsBarProps {
  /** Was gerade eingestellt ist -- wird beim Speichern uebernommen. */
  current: { filters: Record<string, unknown>; columns: string[]; sort: string };
  activeViewId: string | null;
  onApply: (view: SavedView | null) => void;
}

/**
 * Gespeicherte Ansichten -- F1-31, K-30.
 *
 * Eine geteilte Ansicht traegt ein Kennzeichen und bleibt in fremder Hand:
 * Sehen darf sie jeder im Mandanten, aendern nur ihr Urheber. Der Server
 * setzt das durch; die Oberflaeche blendet den Loeschknopf nur aus, damit
 * niemand auf einen Fehler laeuft, den er vorher haette sehen koennen.
 *
 * **Aendern statt loeschen und neu anlegen.** Eine Ansicht, die man nur
 * wegwerfen und neu bauen kann, wird beim dritten Mal "Offene Tickets (2)"
 * heissen -- und die geteilte Fassung, auf die Kollegen ihre Lesezeichen
 * gesetzt haben, waere fort. "Aktualisieren" schreibt Filter, Spalten und
 * Sortierung von jetzt in die aktive Ansicht; "Umbenennen" nur den Namen und
 * die Freigabe.
 */
export function SavedViewsBar({ current, activeViewId, onApply }: SavedViewsBarProps) {
  const { t } = useTranslation();
  const views = useSavedViews();
  const create = useCreateSavedView();
  const remove = useDeleteSavedView();
  const update = useUpdateSavedView();
  const session = useSession();

  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  // Gesetzt heisst: Der Kasten benennt um, statt neu anzulegen.
  const [renaming, setRenaming] = useState<string | null>(null);

  const active = views.data?.find((view) => view.id === activeViewId) ?? null;
  const isMine = active !== null && active.owner_id === session.data?.user_id;

  const closePanel = () => {
    setSaving(false);
    setRenaming(null);
    setName('');
    setShared(false);
  };

  return (
    <div className="saved-views">
      <div className="toolbar" role="group" aria-label={t('views.title')}>
        <Button
          variant={activeViewId === null ? 'secondary' : 'ghost'}
          aria-pressed={activeViewId === null}
          onClick={() => {
            onApply(null);
          }}
        >
          {t('views.none')}
        </Button>

        {(views.data ?? []).map((view) => (
          <Button
            key={view.id}
            variant={activeViewId === view.id ? 'secondary' : 'ghost'}
            aria-pressed={activeViewId === view.id}
            onClick={() => {
              onApply(view);
            }}
          >
            {view.name}
            {view.is_shared ? (
              <>
                {' '}
                <Badge tone="info">{t('views.shared')}</Badge>
              </>
            ) : null}
          </Button>
        ))}

        <span className="shell__spacer" />

        {isMine ? (
          <>
            <Button
              variant="ghost"
              busy={update.isPending}
              title={t('views.updateHint')}
              onClick={() => {
                update.mutate({
                  viewId: active.id,
                  payload: {
                    filters: current.filters,
                    columns: current.columns,
                    sort: current.sort,
                  },
                });
              }}
            >
              {t('views.update')}
            </Button>
            <Button
              variant="ghost"
              aria-expanded={renaming === active.id}
              onClick={() => {
                if (renaming === active.id) {
                  closePanel();
                  return;
                }
                setSaving(false);
                setRenaming(active.id);
                setName(active.name);
                setShared(active.is_shared);
              }}
            >
              {t('views.rename')}
            </Button>
            <Button
              variant="ghost"
              busy={remove.isPending}
              onClick={() => {
                remove.mutate(active.id, {
                  onSuccess: () => {
                    onApply(null);
                  },
                });
              }}
            >
              {t('views.delete')}
            </Button>
          </>
        ) : null}

        <Button
          variant="ghost"
          aria-expanded={saving}
          onClick={() => {
            if (saving) {
              closePanel();
              return;
            }
            setRenaming(null);
            setName('');
            setShared(false);
            setSaving(true);
          }}
        >
          {t('views.save')}
        </Button>
      </div>

      {saving || renaming !== null ? (
        <div className="row">
          <input
            className="field__control"
            value={name}
            aria-label={t('views.name')}
            placeholder={t('views.name')}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={shared}
              onChange={(event) => {
                setShared(event.target.checked);
              }}
            />
            {t('views.shareWithTeam')}
          </label>
          <Button
            variant="primary"
            disabled={!name.trim()}
            busy={create.isPending || update.isPending}
            onClick={() => {
              if (renaming !== null) {
                // Nur Name und Freigabe: Filter und Spalten aendert
                // "Aktualisieren", und zwar ausdruecklich. Wer umbenennt,
                // will nicht nebenbei den Zuschnitt ueberschreiben.
                update.mutate(
                  { viewId: renaming, payload: { name: name.trim(), is_shared: shared } },
                  { onSuccess: closePanel },
                );
                return;
              }
              create.mutate(
                {
                  name: name.trim(),
                  is_shared: shared,
                  filters: current.filters,
                  columns: current.columns,
                  sort: current.sort,
                  sort_order: 0,
                },
                {
                  onSuccess: (view) => {
                    closePanel();
                    onApply(view);
                  },
                },
              );
            }}
          >
            {t('app.save')}
          </Button>
        </div>
      ) : null}

      {create.isError ? <ErrorNotice error={create.error} /> : null}
      {update.isError ? <ErrorNotice error={update.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
    </div>
  );
}
